import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert, AlertTitle,
    List, ListItem, ListItemIcon, ListItemText, Collapse, IconButton, Box, Badge, Stack
} from '@mui/material';
import {
    Warning as WarningIcon,
    Error as ErrorIcon,
    CheckCircle as SuccessIcon,
    ExpandMore as ExpandIcon,
    ExpandLess as CollapseIcon,
    BugReport as BugIcon,
    ArrowForward as GoIcon
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const TroubleshootingAlert = () => {
    const [issues, setIssues] = useState([]);
    const [open, setOpen] = useState(false);
    const [expanded, setExpanded] = useState({});
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        checkForIssues();
        // Verificar a cada 30 segundos
        const interval = setInterval(checkForIssues, 30000);
        return () => clearInterval(interval);
    }, [location.pathname]);

    const checkForIssues = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return;

            // Verificar status do sistema usando endpoints corretos
            const [histRes, dashRes] = await Promise.all([
                axios.get(`${API_URL}/historico_custos`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
                axios.get(`${API_URL}/dashboard/months`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
            ]);

            const detectedIssues = [];

            // histRes.data é um array de objetos com { centro, valores (array de {mes, custo}) }
            // dashRes.data é um array de meses/competências disponíveis

            const hasCosts = histRes.data && Array.isArray(histRes.data) && histRes.data.length > 0;
            const hasMonths = dashRes.data && Array.isArray(dashRes.data) && dashRes.data.length > 0;

            // 1. Sem dados de custo
            if (!hasCosts) {
                detectedIssues.push({
                    id: 'no-costs',
                    severity: 'error',
                    title: 'Nenhum custo processado',
                    description: 'O sistema não possui dados de custos. Sem custos, não é possível gerar análises ou relatórios.',
                    solution: 'Acesse a página de Processamento e faça upload do arquivo de Balancete.',
                    action: { label: 'Ir para Processamento', path: '/processamento' }
                });
            }

            // 2. Verificar se usuário está na página errada (tentando gerar relatório sem dados)
            if (location.pathname.includes('/relatorio') || location.pathname.includes('/conferencia')) {
                if (!hasCosts) {
                    detectedIssues.push({
                        id: 'report-no-data',
                        severity: 'error',
                        title: 'Impossível gerar relatório',
                        description: 'Você está na página de relatórios, mas não há dados processados.',
                        solution: 'Primeiro, processe dados de custos.',
                        action: { label: 'Ir para Processamento', path: '/processamento' }
                    });
                }
            }

            // 3. Verificar se está tentando usar IA sem dados suficientes
            if (location.pathname.includes('/ai-analysis')) {
                if (!hasMonths || dashRes.data.length < 2) {
                    detectedIssues.push({
                        id: 'ai-insufficient-data',
                        severity: 'warning',
                        title: 'Dados insuficientes para IA',
                        description: 'As análises de IA requerem pelo menos 2 meses de dados para gerar insights significativos.',
                        solution: 'Processe mais competências para obter análises mais precisas.',
                        action: { label: 'Processar Mais Dados', path: '/processamento' }
                    });
                }
            }

            setIssues(detectedIssues);

        } catch (error) {
            console.error('Erro ao verificar problemas:', error);
        }
    };

    const handleToggle = (id) => {
        setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const handleAction = (path) => {
        navigate(path);
        setOpen(false);
    };

    const getSeverityColor = (severity) => {
        switch (severity) {
            case 'error': return 'error';
            case 'warning': return 'warning';
            case 'success': return 'success';
            default: return 'info';
        }
    };

    const getSeverityIcon = (severity) => {
        switch (severity) {
            case 'error': return <ErrorIcon />;
            case 'warning': return <WarningIcon />;
            case 'success': return <SuccessIcon />;
            default: return <BugIcon />;
        }
    };

    if (issues.length === 0) return null;

    return (
        <>
            {/* Ícone fixo no canto superior direito */}
            <IconButton
                onClick={() => setOpen(true)}
                sx={{
                    position: 'fixed',
                    top: 16,
                    right: 16,
                    zIndex: 1200,
                    bgcolor: issues.some(i => i.severity === 'error') ? 'error.main' : 'warning.main',
                    color: 'white',
                    '&:hover': {
                        bgcolor: issues.some(i => i.severity === 'error') ? 'error.dark' : 'warning.dark',
                    },
                    boxShadow: 3
                }}
            >
                <Badge
                    badgeContent={issues.length}
                    color={issues.some(i => i.severity === 'error') ? 'error' : 'warning'}
                    sx={{
                        '& .MuiBadge-badge': {
                            bgcolor: 'white',
                            color: issues.some(i => i.severity === 'error') ? 'error.main' : 'warning.main',
                            fontWeight: 'bold'
                        }
                    }}
                >
                    <WarningIcon />
                </Badge>
            </IconButton>

            {/* Dialog de troubleshooting */}
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <BugIcon />
                        <span>Troubleshooting - Problemas Detectados</span>
                    </Stack>
                </DialogTitle>

                <DialogContent sx={{ mt: 2 }}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        O sistema detectou {issues.length} problema(s) que podem afetar seu trabalho.
                        Veja abaixo as soluções sugeridas.
                    </Alert>

                    <List>
                        {issues.map((issue, idx) => (
                            <Box key={issue.id} sx={{ mb: 2 }}>
                                <Alert
                                    severity={getSeverityColor(issue.severity)}
                                    icon={getSeverityIcon(issue.severity)}
                                    action={
                                        <IconButton
                                            size="small"
                                            onClick={() => handleToggle(issue.id)}
                                        >
                                            {expanded[issue.id] ? <CollapseIcon /> : <ExpandIcon />}
                                        </IconButton>
                                    }
                                >
                                    <AlertTitle sx={{ fontWeight: 'bold' }}>{issue.title}</AlertTitle>
                                    {issue.description}
                                </Alert>

                                <Collapse in={expanded[issue.id]}>
                                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mt: 1 }}>
                                        <Stack spacing={2}>
                                            <Box>
                                                <strong>💡 Solução:</strong>
                                                <p style={{ margin: '8px 0' }}>{issue.solution}</p>
                                            </Box>
                                            {issue.action && (
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    endIcon={<GoIcon />}
                                                    onClick={() => handleAction(issue.action.path)}
                                                    size="small"
                                                >
                                                    {issue.action.label}
                                                </Button>
                                            )}
                                        </Stack>
                                    </Box>
                                </Collapse>
                            </Box>
                        ))}
                    </List>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Fechar</Button>
                    <Button variant="contained" onClick={checkForIssues}>
                        Verificar Novamente
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default TroubleshootingAlert;
